import {SkiTestEntity} from '~/types';

export const PAGINATION_SIZE = 8;
export const DEFAULT_GRID_IMG_LOAD_EAGER_COUNT = 4;
export const ATTR_LOADING_EAGER = 'eager';

export function getImageLoadingPriority(
  index: number,
  maxEagerLoadCount = DEFAULT_GRID_IMG_LOAD_EAGER_COUNT,
) {
  return index < maxEagerLoadCount ? ATTR_LOADING_EAGER : undefined;
}

export const SANITY_CONTENT_TYPES = {
  SITE_SETTINGS: 'siteSettings',
  HOME_PAGE: 'homePage',
  ACTIVITY_PAGE: 'activityPage',
  LAYOUT: 'layout',
  HERO: 'heroComponent',
  CARD_GRID: 'cardGrid',
  HERO_WITH_BOTTOM_SECTION: 'heroWithBottomSection',
  FIFTY_FIFTY_HERO_COMPONENT: 'fiftyFiftyHeroComponent',
  BRANDS_COMPONENT: 'brandsComponent',
  TWO_CARDS_WITH_BOTTOM_SECTION_COMPONENT: 'twoCardsWithBottomSectionComponent',
  PRODUCT_CAROUSEL: 'productCarousel',
  BLOG: 'blog',
  BLOG_HOMEPAGE: 'blogHomePage',
  BLOG_FEATURED: 'blogFeatured',
  TABBED_THREE_CARDS_SECONDARY: 'tabbedThreeCardsSecondary',
  COPY: 'copy',
  PAGE: 'page',
  THREE_CARD_SECTION: 'threeCardSection',
  PRODUCT: 'product',
  COLLECTION: 'collection',
  CONTENT_MODAL: 'contentModal',
  PRODUCT_SPECIFICATION: 'productSpecification',
  COLUMN: 'column',
  TABLE: 'tableInternal',
  ACCORDION: 'accordion',
  THREE_CARDS_SECONDARY: 'threeCardsSecondary',
  STORE: 'store',
  PROMO_BADGE: 'promoBadge',
  CONTACT_FORM: 'contactForm',
  PRODUCT_COLOR: 'productColor',
  SKI_TEST: 'skiTest',
  SKI_TESTER: 'skiTester',
  SKI_TEST_CATEGORY: 'skiTestCategory',
  SKI_TEST_PAGE: 'skiTestPage',
  HEADER: 'header',
  MENU_LINK: 'menuLink',
  MEGA_MENU: 'megaMenu',
  MEGA_MENU_ITEM_LINK: 'megaMenuItemLink',
  MEGA_MENU_COLUMN: 'megaMenuColumn',
  MEGA_MENU_COLUMN_GROUP: 'megaMenuColumnGroup',
  MEGA_MENU_ITEM: 'megaMenuItem',
  PRODUCT_GALLERY: 'productGallery',
  BREADCRUMB: 'breadcrumb',
  PRODUCT_HERO: 'productHero',
} as const;

export const SITE_NAME = 'SkiEssentials' as const;
export const SITE_DESCRIPTION =
  'From skis to boots and bindings, SkiEssentials has all the ski equipment you need to hit the slopes. Browse our ski gear now.' as const;

export const CUSTOM_ATTRIBUTES_KEYS = {
  ITEM_VENDOR: '_VENDOR',
  ITEM_PRODUCT_TYPE: '_PRODUCT_TYPE',
  // ADDON
  ADDON_PARENT_PRODUCT_VARIANT_ID: '_ADDON_PARENT_PRODUCT_ID',
  ADDON_PARENT_PRODUCT_TITLE: '_ADDON_PARENT_PRODUCT_TITLE',
  ADDON_PRODUCT_VARIANT_ID: '_ADDON_PRODUCT_VARIANT_ID',
  ADDON_PRODUCT_NAME: '_ADDON_PRODUCT_NAME',
  ADDON_PRODUCT_PRICE: '_ADDON_PRODUCT_PRICE',
  BOPIS_ONLY: '_BOPIS_ONLY',
  // BUNDLE
  BUNDLE_ATTRIBUTE_ID: '_Bundle_ID',
  BUNDLE_ATTRIBUTE_MAIN: '!Bundle_Main',
  BUNDLE_ATTRIBUTE_TYPE: '_Bundle_Type',
  BUNDLE_ATTRIBUTE_BUNDLE: '_Bundle',
  BUNDLE_ATTRIBUTE_PRICE: '_bundle_price',
  BUNDLE_ATTRIBUTE_TOTAL_PRICE: '_bundle_price_total',
  BUNDLE_ATTRIBUTE_QUANTITY: '_Bundle_Quantity',
  BUNDLE_ATTRIBUTE_HASH: 'Bundle_hash',

  BUNDLE_ATTRIBUTE_BOOT_SIZE: '_bundle_boot_size',
  BUNDLE_ATTRIBUTE_POLE_SIZE: '_bundle_pole_size',
};

export const DRAFT = true;

export const SORT_KEYS = {
  FEATURED: 'FEATURED',
  BEST_SELLING: 'BEST_SELLING',
  PRICE: 'PRICE',
  PRICE_REVERSE: 'PRICE_REVERSE',
  CREATED: 'CREATED',
  CREATED_REVERSE: 'CREATED_REVERSE',
  TITLE: 'TITLE',
  TITLE_REVERSE: 'TITLE_REVERSE',
};

export const SORT_OPTIONS = [
  {
    key: SORT_KEYS.FEATURED,
    value: 'featured',
    reverse: false,
    label: 'FEATURED',
    labelMobile: 'Featured',
  },
  {
    key: SORT_KEYS.BEST_SELLING,
    value: 'best-selling',
    reverse: false,
    label: 'BEST SELLERS',
    labelMobile: 'Best Sellers',
  },
  {
    key: SORT_KEYS.PRICE,
    value: 'price-ascending',
    reverse: false,
    label: 'PRICE: LOW-HIGH',
    labelMobile: 'Price: Low-High',
  },
  {
    key: SORT_KEYS.PRICE_REVERSE,
    value: 'price-descending',
    reverse: true,
    label: 'PRICE: HIGH-LOW',
    labelMobile: 'Price: High-Low',
  },
  {
    key: SORT_KEYS.CREATED,
    value: 'created-descending',
    reverse: false,
    label: 'NEWEST - OLDEST',
    labelMobile: 'Newest - Oldest',
  },
  {
    key: SORT_KEYS.CREATED_REVERSE,
    value: 'created-ascending',
    reverse: true,
    label: 'OLDEST - NEWEST',
    labelMobile: 'Oldest - Newest',
  },
  {
    key: SORT_KEYS.TITLE,
    value: 'title-ascending',
    reverse: false,
    label: 'ALPHABETICALLY A-Z',
    labelMobile: 'Alphabetically: A-Z',
  },
  {
    key: SORT_KEYS.TITLE_REVERSE,
    value: 'title-descending',
    reverse: true,
    label: 'ALPHABETICALLY Z-A',
    labelMobile: 'Alphabetically: Z-A',
  },
];

export const SORT_OPTIONS_OBJECT = SORT_OPTIONS.reduce((acc, value) => {
  acc[value.key] = value;
  return acc;
}, {} as {[key: string]: typeof SORT_OPTIONS[0]});

export const QUERY_PARAM_KEYS = {
  ADDON: 'addon',
};

export const TAGS = {
  BOPIS_ONLY: 'BOPIS-ONLY',
  PRODUCT_TYPE_BOOT: 'Boot',
  PRODUCT_TYPE_SKI: 'Ski',
  PRODUCT_TYPE_POLE: 'Pole',
  PRODUCT_TYPE_BINDING: 'Binding',
  PRODUCT_HIDE: 'pfs:hidden:hide',
  COMING_SOON: 'Coming Soon',
};

export const PRODUCT_TYPE = {
  BOOT: 'boot',
  SKI: 'ski',
  POLE: 'pole',
  BINDING: 'binding',
  PRODUCT: 'product',
  VARIANT: 'variant',
};

export const BOOST_PRODUCT_BASE_URL = `${
  import.meta.env.PUBLIC_BOOST_PRODUCT_URL
}`;
export const BOOST_DEFAULT_PARAMS = `shop=${
  import.meta.env.PUBLIC_STORE_DOMAIN_BASE
}`;
export const BOOST_DEFAULT_PAGE_SIZE = 48;

export const FILTER_TYPES = {
  TAG: 'tag',
  COLLECTION: 'collection',
  VENDOR: 'vendor',
  PRICE: 'price',
  OPT_BRAKE_SIZE: 'opt_brake_size',
  STOCK: 'stock',
  COLOR: 'color',
};

export const FILTER_SELECT_TYPES = {
  SINGLE: 'single',
  MULTIPLE: 'multiple',
  RANGE: 'range',
  LINK: 'link',
};

export const BOOST_FILTER_PREFIX = 'pf_';

export const BLOG_PATH = '/Chairlift-Chat';

export const BLOG_CATEGORIES = [
  {
    handle: 'All',
    title: 'ALL',
  },
  {
    handle: 'Helpful-Hints',
    title: 'HELPFUL HINTS',
  },
  {
    handle: 'Interviews',
    title: 'INTERVIEWS',
  },
  {
    handle: 'Ski-Comparisons',
    title: 'SKI COMPARISONS',
  },
  {
    handle: 'Ski-Industry-News',
    title: 'SKI INDUSTRY NEWS',
  },
  {
    handle: 'Ski-Reviews',
    title: 'SKI REVIEWS',
  },
  {
    handle: 'Ski-Stories',
    title: 'SKI STORIES',
  },
  {
    handle: 'Ski-Technology',
    title: 'SKI TECHNOLOGY',
  },
  {
    handle: 'Top-5-Fridays',
    title: 'TOP FIVE FRIDAYS',
  },
  {
    handle: 'Uncategorized',
    title: 'UNCATEGORIZED',
  },
];

export const PASSWORD_MIN_LENGTH = 8;
export const BOOST_REQUEST_HIDE_TAG = 'hide';
export const BOOST_VARIANT_AVAILABLE_OPTION = 'variant_available';

export const ELEMENT_IDS = {
  HEADER: 'header',
  ADD_TO_CART: 'addToCart',
  CHAIRLIFT_CHAT_NAVIGATION: 'chairliftChatNavigation',
  PRODUCT_STICKY_HEADER: 'productStickyHeader',
  // This ID is used in legacy ski test HTML content
  SKI_COMMENTS: 'ski-comments',
  GORGIAS_CHAT_BUNDLE_LOADER: 'gorgias-chat-bundle-loader',
  GORGIAS_CHAT_BUNDLE: 'gorgias-chat-bundle',
};

export const SKI_TEST_SORT_KEYS = {
  SKI_NAME_A_Z: 'title',
  SKI_NAME_Z_A: '-title',
  PRICE_LOW_HIGH: 'price',
  PRICE_HIGH_LOW: '-price',
};

export const SKI_TEST_SORT_CONFIGS = {
  [SKI_TEST_SORT_KEYS.SKI_NAME_A_Z]: {
    label: 'Ski Name (A - Z)',
    sort: (a: SkiTestEntity, b: SkiTestEntity) => {
      const aTitle = a.title ?? '';
      const bTitle = b.title ?? '';

      if (aTitle < bTitle) {
        return -1;
      }
      if (aTitle > bTitle) {
        return 1;
      }
      return 0;
    },
  },
  [SKI_TEST_SORT_KEYS.SKI_NAME_Z_A]: {
    label: 'Ski Name (Z - A)',
    sort: (a: SkiTestEntity, b: SkiTestEntity) => {
      const aTitle = a.title ?? '';
      const bTitle = b.title ?? '';

      if (bTitle < aTitle) {
        return -1;
      }
      if (bTitle > aTitle) {
        return 1;
      }
      return 0;
    },
  },
  [SKI_TEST_SORT_KEYS.PRICE_LOW_HIGH]: {
    label: 'Price (Low - High)',
    sort: (a: SkiTestEntity, b: SkiTestEntity) =>
      (a.price ?? 0) - (b.price ?? 0),
  },
  [SKI_TEST_SORT_KEYS.PRICE_HIGH_LOW]: {
    label: 'Price (High - Low)',
    sort: (a: SkiTestEntity, b: SkiTestEntity) =>
      (b.price ?? 0) - (a.price ?? 0),
  },
};

export const TEST_PRODUCT_TYPES = {
  SKI: 'ski',
  SNOWBOARD: 'snowboard',
  NORDIC_SKI: 'nordic-ski',
};

// Ski/Snowboard Test Filter Names
export const FILTER_NAMES = {
  BRANDS: 'BRANDS',
  GENDER: 'GENDER',
  SKI_TYPE: 'SKI TYPE',
  ABILITY_LEVEL: 'ABILITY LEVEL',
};

export const I_DONT_KNOW_OPTION = "I don't know";

export const SITE_DOMAIN_CURRENT_BASE = import.meta.env.PUBLIC_LIVE_SITE_URL;

// Ski/Snowboard Test Filters
export const filterBrands = {
  name: FILTER_NAMES.BRANDS,
  type: 'categories',
  options: [
    {
      label: 'Armada',
      value: 'armada',
    },
    {
      label: 'Atomic',
      value: 'atomic',
    },
    {
      label: 'Black Crows',
      value: 'black-crows',
    },
    {
      label: 'Blizzard',
      value: 'blizzard',
    },
    {
      label: 'DPS',
      value: 'dps',
    },
    {
      label: 'Dynastar',
      value: 'dynastar',
    },
    {
      label: 'Elan',
      value: 'elan',
    },
    {
      label: 'Faction',
      value: 'faction',
    },
    {
      label: 'Fischer',
      value: 'fischer',
    },
    {
      label: 'Head',
      value: 'head',
    },
    {
      label: 'K2',
      value: 'k2',
    },
    {
      label: 'Kastle',
      value: 'kastle',
    },
    {
      label: 'Liberty',
      value: 'liberty',
    },
    {
      label: 'Line',
      value: 'line',
    },
    {
      label: 'Nordica',
      value: 'nordica',
    },
    {
      label: 'Rossignol',
      value: 'rossignol',
    },
    {
      label: 'Salomon',
      value: 'salomon',
    },
    {
      label: 'Scott',
      value: 'scott',
    },
    {
      label: 'Stockli',
      value: 'stockli',
    },
    {
      label: 'Volkl',
      value: 'volkl',
    },
  ],
};
export const filterBrandValues = filterBrands.options.map((i) => i.value);

export const filterBrandsSnowboard = {
  name: FILTER_NAMES.BRANDS,
  type: 'categories',
  options: [
    {
      label: 'Arbor',
      value: 'arbor',
    },
    {
      label: 'Jones',
      value: 'jones',
    },
    {
      label: 'K2',
      value: 'k2',
    },
    {
      label: 'Never Summer',
      value: 'never-summer',
    },
    {
      label: 'Ride',
      value: 'ride',
    },
    {
      label: 'Rossignol',
      value: 'rossignol',
    },
    {
      label: 'Salomon',
      value: 'salomon',
    },
    {
      label: 'Whitespace',
      value: 'whitespace',
    },
  ],
};

export const filterBrandSnowboardValues = filterBrandsSnowboard.options.map(
  (i) => i.value,
);

export const filterGender = {
  name: FILTER_NAMES.GENDER,
  type: 'tags',
  options: [
    {
      label: "Men's",
      value: 'mens',
    },
    {
      label: "Women's",
      value: 'womens',
    },
  ],
};

export const filterGenderValues = filterGender.options.map((i) => i.value);

export const filterSkiType = {
  name: FILTER_NAMES.SKI_TYPE,
  type: 'tags',
  options: [
    {
      label: 'All-Mountain',
      value: 'type-all-mountain',
    },
    {
      label: 'Freeride',
      value: 'type-freeride',
    },
    {
      label: 'Frontside',
      value: 'type-frontside',
    },
  ],
};

export const filterSkiTypeValues = filterSkiType.options.map((i) => i.value);

export const filterAbilityLevel = {
  name: FILTER_NAMES.ABILITY_LEVEL,
  type: 'tags',
  options: [
    {
      label: 'Beginner',
      value: 'ability-beginner',
    },
    {
      label: 'Intermediate',
      value: 'ability-intermediate',
    },
    {
      label: 'Advanced Intermediate',
      value: 'ability-advanced-intermediate',
    },
    {
      label: 'Advanced',
      value: 'ability-advanced',
    },
    {
      label: 'Expert',
      value: 'ability-expert',
    },
  ],
};

export const filterAbilityLevelValues = filterAbilityLevel.options.map(
  (i) => i.value,
);

// Ski/Snowboard Category Sidebar
export const mensSkis = (year: number, type: string) => {
  if (type == TEST_PRODUCT_TYPES.SKI) {
    return {
      name: "MEN'S SKIS",
      children: [
        {
          name: "ALL MEN'S SKIS",
          href:
            year === 2020
              ? `/${year}-${type}-test/category/mens-skis`
              : `/${year}-${type}-test/category/mens`,
          exclude: [2019, 2018],
        },
        {
          name: "MEN'S ALL MOUNTAIN",
          href: `/${year}-${type}-test/category/mens-all-mountain`,
        },
        {
          name: "MEN'S FREERIDE",
          href: `/${year}-${type}-test/category/mens-freeride`,
        },
        {
          name: "MEN'S FRONTSIDE",
          href: `/${year}-${type}-test/category/mens-frontside`,
        },
      ],
    };
  } else if (type === TEST_PRODUCT_TYPES.SNOWBOARD) {
    return {
      name: "MEN'S SNOWBOARDS",
      children: [
        {
          name: "ALL MEN'S SNOWBOARDS",
          href: `/${year}-${type}-test/category/mens`,
        },
        {
          name: "MEN'S ALL MOUNTAIN",
          href: `/${year}-${type}-test/category/mens-all-mountain`,
        },
        {
          name: "MEN'S FREERIDE",
          href: `/${year}-${type}-test/category/mens-freeride`,
        },
        {
          name: "MEN'S PARK",
          href: `/${year}-${type}-test/category/mens-park`,
        },
      ],
    };
  }

  return undefined;
};
export const womensSkis = (year: number, type: string) => {
  if (type === TEST_PRODUCT_TYPES.SKI) {
    return {
      name: "WOMEN'S SKIS",
      children: [
        {
          name: "ALL WOMEN'S SKIS",
          href:
            year === 2020
              ? `/${year}-${type}-test/category/womens-skis`
              : `/${year}-${type}-test/category/womens`,
          exclude: [2019, 2018],
        },
        {
          name: "WOMEN'S ALL MOUNTAIN",
          href: `/${year}-${type}-test/category/womens-all-mountain`,
        },
        {
          name: "WOMEN'S FREERIDE",
          href: `/${year}-${type}-test/category/womens-freeride`,
        },
        {
          name: "WOMEN'S FRONTSIDE",
          href: `/${year}-${type}-test/category/womens-frontside`,
        },
      ],
    };
  } else if (type === TEST_PRODUCT_TYPES.SNOWBOARD) {
    return {
      name: "WOMEN'S SNOWBOARDS",
      children: [
        {
          name: "ALL WOMEN'S SNOWBOARDS",
          href: `/${year}-${type}-test/category/womens`,
        },
        {
          name: "WOMEN'S ALL MOUNTAIN",
          href: `/${year}-${type}-test/category/womens-all-mountain`,
        },
        {
          name: "WOMEN'S FREERIDE",
          href: `/${year}-${type}-test/category/womens-freeride`,
        },
        {
          name: "WOMEN'S PARK",
          href: `/${year}-${type}-test/category/womens-park`,
        },
      ],
    };
  }

  return undefined;
};
export const brands = (year: number, type: string) => {
  if (type === TEST_PRODUCT_TYPES.SKI) {
    return {
      name: 'BRANDS',
      children: [
        {
          name: 'ARMADA',
          href: `/${year}-ski-test/category/brands/armada`,
        },
        {
          name: 'ATOMIC',
          href: `/${year}-ski-test/category/brands/atomic`,
        },
        {
          name: 'BLACK CROWS',
          href: `/${year}-ski-test/category/brands/black-crows`,
          exclude: [2019, 2018],
        },
        {
          name: 'BLIZZARD',
          href: `/${year}-ski-test/category/brands/blizzard`,
        },
        {
          name: 'DPS',
          href: `/${year}-ski-test/category/brands/dps`,
        },
        {
          name: 'DYNASTAR',
          href: `/${year}-ski-test/category/brands/dynastar`,
        },
        {
          name: 'ELAN',
          href: `/${year}-ski-test/category/brands/elan`,
        },
        {
          name: 'FACTION',
          href: `/${year}-ski-test/category/brands/faction`,
          exclude: [2022, 2021, 2020, 2019, 2018],
        },
        {
          name: 'FISCHER',
          href: `/${year}-ski-test/category/brands/fischer`,
        },
        {
          name: 'HEAD',
          href: `/${year}-ski-test/category/brands/head`,
        },
        {
          name: 'K2',
          href: `/${year}-ski-test/category/brands/k2`,
        },
        {
          name: 'KASTLE',
          href: `/${year}-ski-test/category/brands/kastle`,
        },
        {
          name: 'LIBERTY',
          href: `/${year}-ski-test/category/brands/liberty`,
          exclude: [2018],
        },
        {
          name: 'LINE',
          href: `/${year}-ski-test/category/brands/line`,
          exclude: [2019, 2018],
        },
        {
          name: 'NORDICA',
          href: `/${year}-ski-test/category/brands/nordica`,
        },
        {
          name: 'ROSSIGNOL',
          href: `/${year}-ski-test/category/brands/rossignol`,
        },
        {
          name: 'SALOMON',
          href: `/${year}-ski-test/category/brands/salomon`,
        },
        {
          name: 'SCOTT',
          href: `/${year}-ski-test/category/brands/scott`,
          exclude: [2025, 2022, 2021, 2020, 2019, 2018],
        },
        {
          name: 'STOCKLI',
          href: `/${year}-ski-test/category/brands/stockli`,
          exclude: [2019, 2018],
        },
        {
          name: 'VOLKL',
          href: `/${year}-ski-test/category/brands/volkl`,
        },
      ],
    };
  } else if (type === TEST_PRODUCT_TYPES.NORDIC_SKI) {
    return {
      name: 'BRANDS',
      children: [
        {
          name: 'ALPINA',
          href: `/${year}-nordic-ski-test/category/brands/alpina`,
        },
        {
          name: 'FISCHER',
          href: `/${year}-nordic-ski-test/category/brands/fischer`,
        },
        {
          name: 'ROSSIGNOL',
          href: `/${year}-nordic-ski-test/category/brands/rossignol`,
        },
        {
          name: 'SALOMON',
          href: `/${year}-nordic-ski-test/category/brands/salomon`,
        },
      ],
    };
  } else if (type === TEST_PRODUCT_TYPES.SNOWBOARD) {
    return {
      name: 'BRANDS',
      children: [
        {
          name: 'ARBOR',
          href: `/${year}-snowboard-test/category/brands/arbor`,
        },
        {
          name: 'JONES',
          href: `/${year}-snowboard-test/category/brands/jones`,
          exclude: [2023],
        },
        {
          name: 'K2',
          href: `/${year}-snowboard-test/category/brands/k2`,
        },
        {
          name: 'NEVER SUMMER',
          href: `/${year}-snowboard-test/category/brands/never-summer`,
          exclude: [2023],
        },
        {
          name: 'RIDE',
          href: `/${year}-snowboard-test/category/brands/ride`,
        },
        {
          name: 'ROSSIGNOL',
          href: `/${year}-snowboard-test/category/brands/rossignol`,
        },
        {
          name: 'SALOMON',
          href: `/${year}-snowboard-test/category/brands/salomon`,
        },
        {
          name: 'WHITESPACE',
          href: `/${year}-snowboard-test/category/brands/whitespace`,
          exclude: [2023],
        },
      ],
    };
  }
  return undefined;
};

export const nordicSkis = (year: number) => {
  return {
    name: 'SKIS',
    children: [
      {
        href: `/${year}-nordic-ski-test/category/skis/backcountry-light-touring`,
        name: 'BACKCOUNTRY/LIGHT TOURING',
      },
      {
        href: `/${year}-nordic-ski-test/category/skis/performance-classic`,
        name: 'PERFORMANCE CLASSIC',
      },
      {
        href: `/${year}-nordic-ski-test/category/skis/performance-skate`,
        name: 'PERFORMANCE SKATE',
      },
      {
        href: `/${year}-nordic-ski-test/category/skis/recreational-classic`,
        name: 'RECREATIONAL CLASSIC',
      },
      {
        href: `/${year}-nordic-ski-test/category/skis/recreational-skate`,
        name: 'RECREATIONAL SKATE',
      },
    ],
  };
};

export const skiTesters = (year: number, type: string) => ({
  name: [TEST_PRODUCT_TYPES.SKI, TEST_PRODUCT_TYPES.NORDIC_SKI].includes(type)
    ? 'SKI TESTERS'
    : 'SNOWBOARD TESTERS',
  href: `/${year}-${type}-test/tester`,
});

export const LEGACY_SKI_TESTER_DUPLICATE_SLUGS = {
  'allison-ruschp': 'alli-ruschp',
} as Record<string, string>;

export const ANALYTIC_EVENT_NAMES = {
  EMAIL_SIGNUP: 'Email Signup',
  PAGE_DETAILS_LOADED: 'Page Details Loaded',
  VIEW_SEARCH_RESULTS: 'View Search Results',
  ADD_TO_CART: 'Add to Cart',
  VIEW_CART: 'View Cart',
  VIEW_ITEM: 'View Item',
  VIEW_ITEM_LIST: 'View Item List',
  SELECT_ITEM: 'Select Item',
  NAVIGATION_CLICK: 'Navigation Click',
  BEGIN_CHECKOUT: 'Begin Checkout',
};

export const LOCAL_STORAGE_KEYS = {
  PAGE_RELOAD_ON_ERROR_TIMESTAMP: 'PAGE_RELOAD_ON_ERROR_TIMESTAMP',
};

export const USE_DRAFT_CONTENT =
  import.meta.env.PUBLIC_SANITY_USE_DRAFT_CONTENT === 'true';

export const PRIVATE_SANITY_TOKEN_KEY = '_PRIVATE_SANITY_TOKEN';

export const SESSION_SANITY_TOKEN_KEY = 'sanityToken';

export const DATA_TEST_IDS = {
  ARTICLE_CARD: 'article-card',
  ARTICLE_CARD_TITLE: (title: string) => `${title.toLowerCase()}-article-card`,
  COLLECTION_CARD: 'collection-card',
  COLLECTION_CARD_TITLE: (title: string) =>
    `${title.toLowerCase()}-collection-card`,
  ORDER_CARD: 'order-card',
  ORDER_CARD_HEADING: 'order-card-heading',
  ORDER_ID: 'order-id',
  ORDER_DATE: 'order-date',
  ORDER_FULFILLMENT: 'oredr-fulfillment',
  LINK: (linkName: string) => `${linkName}-link`,
  BUTTON: (buttonName: string) => `${buttonName}-button`,
  INPUT: (inputName: string) => `${inputName}-input`,
  CHECKBOX: (checkboxName: string) => `${checkboxName}-checkbox`,
  ICON: (iconName: string) => `${iconName}-icon`,
  TEXT_AREA: (textAreaName: string) => `${textAreaName}-textarea`,
  PROMOTIONAL_HEADLINE: 'promotional-headline',
  PRODUCT_CARD: 'product-card',
  PRODUCT_CARD_LABEL_VALUE: (value: string) =>
    `${value}-product-card-label-value`,
  PRODUCT_CARD_VARIANTS_COLOR: 'product-card-variants-color',
  PRODUCT_CARD_TITLE: 'product-card-title',
  PRODUCT_PRICE: 'product-price',
  SAVING_PRICE: 'saving-price',
  COMPARE_AT_PRICE: 'compare-at-price',
  VARIANT_SELECTOR: 'variant-selector',
  ACCOUNT_ADDRESS_BOOK: 'account-address-book',
  ACCOUNT_ADDRESS_DELETE: 'account-address-delete',
  ACCOUNT_ADDRESS_EDIT: 'account-address-edit',
  SUBMIT_ERROR: 'submit-error',
  ADDRESS: 'address',
  DEFAULT_ADDRESS: 'default-address',
  ADDRESS_NAME: 'address-name',
  ADDRESS_LINE: (lineName: string) => `${lineName}-line`,
  CREATE_ACCOUNT: 'create-account',
  EMAIL_ERROR: 'email-error',
  PASSWORD_ERROR: 'password-error',
  ACCOUNT_DETAILS: 'account-details',
  ACCOUNT_DETAILS_EDIT: 'account-details-edit',
  ACCOUNT_NAME: 'account-name',
  ACCOUNT_EMAIL: 'account-email',
  ACCOUNT_CONTACT: 'account-contact',
  LOGIN_FORM: 'login-form',
  ACCOUNT_ORDER_HISTORY: 'account-order-history',
  EMPTY_ORDERS: 'empty-orders',
  CART_DETAILS: 'cart-details',
  CART_TOTAL: (total: number) => `${total}-total`,
  CART_EMPTY: 'cart-empty',
  CART_LINE_ITEM: 'cart-line-item',
  CART_LINE_OPTION: 'cart-line-option',
  CART_LINE_QUANTITY: (qty: number) => `${qty}-quantity`,
  BASE_ADDON: 'base-addon',
  BASE_ADDON_ENABLED: 'base-addon-enabled',
  ADDON_MONEY: 'addon-money',
  ORDER_SUMMARY: 'order-summary',
  CHECKOUT_INSTRUCTIONS: 'checkout-instructons',
  LISTBOX_BUTTON: 'listbox-button',
  LISTBOX_OPTION: 'listbox-option',
  CATEGORY_TITLE: 'category-title',
  POST_CATEGORIES: 'post-categories',
  SPINNER: 'spinner',
  TAB_LIST: 'tab-list',
  TAB: (tabNumber: number) => `${tabNumber}-tab`,
  TAB_HEADER_BUTTON: 'tab-header-button',
  TAG: 'tag',
  TEXT_INTERANL: 'text-internal',
  PRODUCT_DETAIL: 'product-detail',
  LONG_DESCRIPTION: 'long-description',
  MODAL: 'modal',
  PRODUCT_DETAIL_HEADER: 'product-detail-header',
  PRODUCT_DETAIL_HEADER_RIGHT: 'product-detail-header-right',
  PRODUCT_FEATURES: 'product-features',
  PRODUCT_FEATURES_CONTENT: 'product-feature-content',
  PRODUCT_FILTERS_TAG: 'product-filters-tag',
  FILTER_TAG: (filterTag: string) => `${filterTag.toLowerCase()}-tag`,
  PRODUCT_FORM: 'product-form',
  BUNDLE_PRODUCT: 'bundle-product',
  BUNDLE_TYPE: (bundleType: string) => `${bundleType}-type`,
  DESKTOP_GALLERY: 'desktop-gallery',
  YOUTUBE_PLAYER: 'youtube-player',
  PRODUCT_GRID: 'product-grid',
  PRODUCT_HEADER: 'product-header',
  PRODUCT_HEADER_TITLE: 'product-header-title',
  PRODUCT_HEADER_STICKY: 'product-header-sticky',
  OPTIONS_COLOR: 'options-color',
  ERROR: 'error',
  PRODUCT_OPTIONS_DROPDOWN: 'product-options-dropdown',
  PRODUCT_OPTIONS_SWATCH: 'product-options-swatch',
  PROMO_BOX: 'promo-box',
  PRODUCT_QUESTIONS: 'product-questions',
  PRODUCT_REVIEWS: 'product-reviews',
  SKU_CONTAINER: 'sku-container',
  PRODUCT_SPECIFICATIONS: 'product-specifications',
  CONTENT_SPECIFICATIONS: 'content-specifications',
  PRODUCT_UPSELL: 'product-upsell',
  ANNOUNCEMENT_BAR: 'announcement-bar',
  ANNOUNCEMENT_CONTENT: 'announcement-content',
  PRODUCT_LENGTH: (productLenght: number) => `${productLenght}-length`,
  PRODUCT_TOTAL: (productTotal?: number) => `${productTotal ?? 0}-total`,
  PRODUCT_LIST_HEADING: 'product-list-heading',
  SORT_LABEL: (labelName: string) => `${labelName}-sort-label`,
  RANGE_FILTER: 'range-filter',
  FILTER_LABEL: (filterName: string) => `${filterName}-filter-label`,
  ACCORDION: 'accordion',
  ACCORDION_ITEM: 'accordion-item',
  BREADCRUMB: 'breadcrumb',
  BREADCRUMB_ITEM: 'breadcrumb-item',
  BRANDS: 'brands',
  BRANDS_TITLE: 'brands-title',
  CARD_GRID: 'card-grid',
  CARD: 'card',
  COMPARE_TYPE_GRID: 'compare-type-grid',
  COMPARE_TYPE_GRID_ITEM: 'compare-type-grid-item',
  COLLECTION_DESCRIPTION: 'collection-description',
  CATEGORY_TYPE_CAROUSEL: 'category-type-carousel',
  FEATURED_COLLECTIONS: 'featured-collections',
  FIFTY_FIFTY: 'fifty-fifty',
  FIFTY_FIFTY_ITEM: 'fifty-fifty-item',
  FIFTY_FIFTY_TITLE: 'fifty-fifty-title',
  FIFTY_FIFTY_DESCRIPTION: 'fifty-fifty-description',
  HERO: 'hero',
  HERO_TITLE: 'hero-title',
  HERO_DESCRIPTION: 'hero-description',
  HERO_WITH_BOTTOM: 'hero-with-bottom',
  HERO_WITH_BOTTOM_TITLE: 'hero-with-bottom-title',
  HERO_WITH_BOTTOM_DESCRIPTION: 'hero-with-bottom-description',
  PRODUCT_CAROUSEL: 'product-carousel',
  PRODUCT_CAROUSEL_TITLE: 'product-carousel-title',
  PRODUCT_CAROUSEL_DESCRIPTION: 'product-carousel-description',
  PRODUCT_TAB_CAROUSEL: 'product-tab-carousel',
  TABBED_THREE_CARDS_SECONDARY: 'tabbed-three-cards-secondary',
  TABBED_THREE_CARDS_SECONDARY_TITLE: 'tabbed-three-cards-secondary-title',
  TABBED_THREE_CARDS_SECONDARY_DESCRIPTION:
    'tabbed-three-cards-secondary-description',
  CARD_SECONDARY: 'card-secondary',
  CARD_SECONDARY_TITLE: 'card-secondary-title',
  CARD_SECONDARY_DESCRIPTION: 'card-secondary-description',
  CARD_TITLE: 'card-title',
  CARD_DESCRIPTION: 'card-description',
  THREE_CARDS_SECONDARY: 'three-cards-secondary',
  THREE_CARDS_SECONDARY_TITLE: 'three-cards-secondary-title',
  THREE_CARDS_SECONDARY_DESCRIPTION: 'three-cards-secondary-description',
  TWO_CARDS_WITH_BOTTOM_SECTION_COMPONENT:
    'two-cards-with-bottom-section-component',
  TWO_CARDS_WITH_BOTTOM_SECTION_ITEM: 'two-cards-with-bottom-section-item',
  TWO_CARDS_WITH_BOTTOM_SECTION_TITLE: 'two-cards-with-bottom-section-title',
  TWO_CARDS_WITH_BOTTOM_SECTION_DESCRIPTION:
    'two-cards-with-bottom-section-description',
  SEARCH_OPENED: 'search-opened',
  ACTIVE_MENU: 'active-menu',
  LEFT_LINK_SECTION: 'left-link-section',
  MEGA_MENU_COLUMN: 'mega-menu-column',
  CART_BADGE: 'cart-badge',
  OVERLAY: (overlayName: string) => `${overlayName}-overlay`,
};
